/**
 * Meeting-notes CRM trigger card variant — Header (always visible in the
 * disclosure trigger), collapsed PreviewBody, and expanded Body.
 */

import { Calendar, Users01 } from '@untitledui/icons';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import type { CrmEventTriggerData } from '@/lib/proposals/core/types';
import { type TimeFormat, useOrgTimeFormat } from '@/lib/time';

import {
  formatMeetingTime,
  getStringValue,
  parseActionItems,
  parseKeyTopics,
} from './helpers';

/**
 * Helper that pulls the meeting-notes fields out of a trigger so the
 * Header and Body components can share the extraction.
 */
function getMeetingNotesData(
  data: CrmEventTriggerData,
  timeFormat: TimeFormat
) {
  const { event } = data.crm_event;
  const title =
    getStringValue(event, 'ahoy_title') || data.crm_event.display_name;
  const summary = getStringValue(event, 'ahoy_summary');
  const notesSource = getStringValue(event, 'ahoy_notes_source');
  const sourceLabel = notesSource
    ? notesSource.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    : null;
  const meeting = data.associated_meeting;
  const meetingTime = meeting?.start_time
    ? formatMeetingTime(meeting.start_time, timeFormat)
    : null;
  const attendees = meeting?.attendees ?? [];
  const actionItems = parseActionItems(
    getStringValue(event, 'ahoy_action_items')
  );
  const keyTopics = parseKeyTopics(getStringValue(event, 'ahoy_key_topics'));
  return {
    title,
    summary,
    sourceLabel,
    meetingTime,
    attendees,
    actionItems,
    keyTopics,
  };
}

/**
 * Meeting-notes header — always visible in the disclosure trigger.
 * Shows title + source. The compact "preview" (meeting time + attendees +
 * summary line-clamp) lives in `MeetingNotesPreviewBody`, rendered into
 * AccordionItem.previewContent so it's visible while collapsed and
 * animates closed when the user expands. The full expanded content
 * (action items, key topics, full summary) lives in `MeetingNotesBody`.
 */
export function MeetingNotesHeader({ data }: { data: CrmEventTriggerData }) {
  // Org default read from context — not threaded through props.
  const { timeFormat } = useOrgTimeFormat();
  const { title, sourceLabel } = getMeetingNotesData(data, timeFormat);
  return (
    <div className='flex-1 min-w-0'>
      <div className='flex items-center gap-2'>
        <span className='text-sm font-medium text-primary truncate'>
          {title}
        </span>
        {sourceLabel && (
          <>
            <span className='text-tertiary flex-shrink-0'>·</span>
            <span className='text-xs text-tertiary flex-shrink-0'>
              {sourceLabel}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/** Compact meeting-notes preview shown while the disclosure is collapsed:
 *  meeting time + attendees in icon form, plus a line-clamped summary. */
export function MeetingNotesPreviewBody({
  data,
  orgId,
}: {
  data: CrmEventTriggerData;
  orgId?: number;
}) {
  // Org default read from context — not threaded through props.
  const { timeFormat } = useOrgTimeFormat();
  const { summary, meetingTime, attendees } = getMeetingNotesData(
    data,
    timeFormat
  );
  if (!summary && !meetingTime && attendees.length === 0) {
    return null;
  }
  return (
    <div className='space-y-1'>
      {(meetingTime || attendees.length > 0) && (
        <div className='text-xs text-secondary flex items-center gap-2 flex-wrap'>
          {meetingTime && (
            <span className='flex items-center gap-1'>
              <Calendar className='w-3 h-3' />
              {meetingTime}
            </span>
          )}
          {attendees.length > 0 && (
            <span className='flex items-center gap-1'>
              <Users01 className='w-3 h-3' />
              {attendees.join(', ')}
            </span>
          )}
        </div>
      )}
      {summary && (
        <div className='text-xs text-tertiary line-clamp-2 overflow-hidden'>
          <MarkdownContent content={summary} size='compact' orgId={orgId} />
        </div>
      )}
    </div>
  );
}

/**
 * Meeting-notes expanded body — rendered into the AccordionItem panel
 * when the disclosure is open. Mounted lazily so the heavy parsing /
 * rendering only runs once the user expands the card.
 */
export function MeetingNotesBody({
  data,
  orgId,
}: {
  data: CrmEventTriggerData;
  orgId?: number;
}) {
  // Org default read from context — not threaded through props.
  const { timeFormat } = useOrgTimeFormat();
  const { summary, meetingTime, attendees, actionItems, keyTopics } =
    getMeetingNotesData(data, timeFormat);
  return (
    <div className='space-y-3'>
      {(meetingTime || attendees.length > 0) && (
        <div>
          <div className='text-xs font-medium text-secondary mb-1'>
            Meeting details
          </div>
          <div className='text-xs text-secondary space-y-1'>
            {meetingTime && (
              <div className='flex items-center gap-1.5'>
                <Calendar className='w-3.5 h-3.5 flex-shrink-0' />
                <span>{meetingTime}</span>
              </div>
            )}
            {attendees.length > 0 && (
              <div className='flex items-start gap-1.5'>
                <Users01 className='w-3.5 h-3.5 flex-shrink-0 mt-0.5' />
                <span>{attendees.join(', ')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {summary && (
        <div>
          <div className='text-xs font-medium text-secondary mb-1'>Summary</div>
          <MarkdownContent
            content={summary}
            className='text-xs text-secondary leading-relaxed'
            size='compact'
            orgId={orgId}
          />
        </div>
      )}

      {actionItems.length > 0 && (
        <div>
          <div className='text-xs font-medium text-secondary mb-1'>
            Action Items
          </div>
          <ul className='text-xs text-secondary space-y-1 list-disc list-inside'>
            {(() => {
              const seen = new Map<string, number>();
              return actionItems.map(item => {
                const count = seen.get(item) ?? 0;
                seen.set(item, count + 1);
                return <li key={`${item}::${count}`}>{item}</li>;
              });
            })()}
          </ul>
        </div>
      )}

      {keyTopics.length > 0 && (
        <div>
          <div className='text-xs font-medium text-secondary mb-1'>
            Key Topics
          </div>
          <div className='flex flex-wrap gap-1.5'>
            {(() => {
              const seen = new Map<string, number>();
              return keyTopics.map(topic => {
                const count = seen.get(topic) ?? 0;
                seen.set(topic, count + 1);
                return (
                  <span
                    key={`${topic}::${count}`}
                    className='px-2 py-0.5 text-xs bg-secondary text-secondary rounded-full'
                  >
                    {topic}
                  </span>
                );
              });
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
